import { useState, useEffect } from 'react';
import { useCategoriesWithGroups } from '../../hooks/useCategories';
import { useCurationStore } from '../../store/curationStore';
import { useMenuStore } from '../../store/menuStore';
import { trackEvent } from '../../utils/analytics';
import { Button, CategoryChip, Modal } from '@citiguide/ui-components';
import './CategoryMenu.css';

interface CategoryMenuProps {
  inline?: boolean;
}

export default function CategoryMenu({ inline = false }: CategoryMenuProps) {
  const { isCategoryMenuOpen, closeAllMenus } = useMenuStore();
  const selectedCategories = useCurationStore((s) => s.state.categories);
  const setCategories = useCurationStore((s) => s.setCategories);
  const clearCurationFilters = useCurationStore((s) => s.clearFilters);
  const [menuStack, setMenuStack] = useState<Array<{ id: string; name: string; type: 'group' | 'category' }[]>>([]);
  const [currentLevel, setCurrentLevel] = useState<Array<{ id: string; name: string; type: 'group' | 'category' }>>([]);

  const { data: categoriesData } = useCategoriesWithGroups();

  useEffect(() => {
    if (categoriesData?.group && categoriesData.group.length > 0) {
      // Audience groups are not shown in this menu.
      const topLevel = categoriesData.group.filter(group => group.dimension !== 'audience').map(group => ({
        id: group.id,
        name: group.name || 'Unnamed group',
        type: 'group' as const,
      }));
      setCurrentLevel(topLevel);
    }
  }, [categoriesData]);

  const handleItemClick = (item: { id: string; name: string; type: 'group' | 'category' }) => {
    if (item.type === 'group') {
      const categoriesInGroup = categoriesData?.category?.filter(cat => cat.gId === item.id && cat.dimension !== 'audience') || [];
      if (categoriesInGroup.length > 0) {
        setMenuStack([...menuStack, currentLevel]);
        setCurrentLevel(categoriesInGroup.map(cat => ({
          id: cat.id,
          name: cat.name || 'Unnamed category',
          type: 'category' as const,
        })));
      }
    } else {
      setCategories([item.id]);
      trackEvent('filter', 'select_category', item.id);
      closeAllMenus();
    }
  };

  const handleBack = () => {
    if (menuStack.length > 0) {
      const previousLevel = menuStack[menuStack.length - 1];
      setMenuStack(menuStack.slice(0, -1));
      setCurrentLevel(previousLevel);
    } else if (!inline) {
      closeAllMenus();
    }
  };

  const handleClearFilters = () => {
    clearCurationFilters();
    trackEvent('filter', 'clear_filters');
    closeAllMenus();
  };

  const menuTitle = menuStack.length > 0 ? 'Select category' : 'Categories';

  const body = (
    <div className="menu-content">
      {menuStack.length > 0 && (
        <Button variant="ghost" size="sm" onClick={handleBack}>← Back</Button>
      )}
      <div className="category-list">
        {currentLevel.length === 0 && (
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', margin: 0 }}>
            No categories available
          </p>
        )}
        {currentLevel.map((item) => (
          <CategoryChip
            key={item.id}
            label={item.type === 'group' ? `${item.name} →` : item.name}
            active={item.type === 'category' && selectedCategories.includes(item.id)}
            style={{ width: '100%', justifyContent: 'flex-start', borderRadius: 'var(--radius-md)' }}
            onClick={() => handleItemClick(item)}
          />
        ))}
      </div>
      <div className="menu-actions">
        <Button variant="danger" size="sm" block onClick={handleClearFilters}>
          Clear all filters
        </Button>
      </div>
    </div>
  );

  if (inline) {
    return (
      <div className="category-menu-inline">
        <h3 className="category-menu-inline__title">{menuTitle}</h3>
        {body}
      </div>
    );
  }

  return (
    <Modal open={isCategoryMenuOpen} title={menuTitle} onClose={closeAllMenus} size="sm">
      {body}
    </Modal>
  );
}
